/**
 * DeckManager — per /design/specs/4_architecture_c4_l3_components.md §3.1
 * and /design/specs/5_game_engine_spec.md §1.2.
 *
 * Pure functions over `Card[]`. No mutable state.
 */

import { type Card, RANKS, SUITS, cardsEqual } from '@/types/card';
import type { RNG } from '@/lib/rng';

export type Deck = Card[];

/** Build a fresh, ordered 52-card deck. */
export function freshDeck(): Deck {
  const deck: Deck = [];
  for (const rank of RANKS) {
    for (const suit of SUITS) {
      deck.push({ rank, suit });
    }
  }
  return deck;
}

/** Return a shuffled copy of `deck`. */
export function shuffle(deck: Deck, rng: RNG): Deck {
  return rng.shuffle(deck);
}

/**
 * Return a copy of `deck` with every card in `toRemove` filtered out.
 * Used to compute the live deck after dealing hero / villain / board cards.
 */
export function removeCards(deck: Deck, toRemove: readonly Card[]): Deck {
  if (toRemove.length === 0) return deck.slice();
  return deck.filter((c) => !toRemove.some((r) => cardsEqual(c, r)));
}
